import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { TenantUsersApi } from "../../../api/common/apiIndex";
import Loader from "../../common/commonUtils/Loader";
import EditEntityModal from "../../common/commonUtils/EditEntityModal";
import showConfirmToast from "../businessUtilities/ConfirmToast";
import { useAuth } from "../../../context/AuthProvider";
import UserProfileCard from "./UserProfileCard";
import RoleManagerModal from "./RoleManagerModal";
import MoreActionsDropdown from "../../common/commonUtils/MoreActionsDropdown";

export default function UserDetailsPage() {
  const { userUuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, hasRole } = useAuth();

  const isOwner = hasRole("BUSINESS_OWNER");
  const isAdmin = hasRole("ADMIN");

  const detailQuery = useQuery({
    queryKey: ["tenant-user", userUuid],
    queryFn: () => TenantUsersApi.getByUuid(userUuid),
    enabled: Boolean(userUuid),
  });

  const user = detailQuery.data;
  const roles = useMemo(() => new Set(user?.roles || []), [user?.roles]);
  const targetIsOwner = roles.has("BUSINESS_OWNER");
  const targetIsAdmin = roles.has("ADMIN");
  const targetIsAgent = roles.has("AGENT");
  const isSelf = currentUser?.id === user?.id;

  const canEditProfile = Boolean(user) && (isOwner || (isAdmin && !targetIsOwner));
  const canManageRoles = Boolean(user) && isOwner;
  const canToggleStatus = Boolean(user) && (isOwner || (isAdmin && targetIsAgent && !targetIsAdmin && !targetIsOwner));
  const canDelete = Boolean(user) && (isOwner || (isAdmin && targetIsAgent && !targetIsAdmin && !targetIsOwner));

  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: ["tenant-users"] });
    queryClient.invalidateQueries({ queryKey: ["agents"] });
    detailQuery.refetch();
  };

  useEffect(() => {
    if (user && targetIsAgent && user.uuid) {
      navigate(`/home/agent/${user.uuid}`, { replace: true });
    }
  }, [user, targetIsAgent, navigate]);

  const handleError = (err, fallback) => {
    const status = err?.response?.status;
    if (status === 403) {
      toast.error(err?.response?.data?.message || "You don't have permission to perform this action.");
      return;
    }
    toast.error(err?.response?.data?.message || err?.message || fallback);
  };

  const updateMutation = useMutation({
    mutationFn: (dto) => TenantUsersApi.update(user.id, dto),
    onSuccess: () => {
      toast.success("User updated.");
      setShowEditModal(false);
      invalidateLists();
    },
    onError: (err) => handleError(err, "Failed to update user."),
  });

  const statusMutation = useMutation({
    mutationFn: (active) => TenantUsersApi.updateStatus(user.id, active),
    onSuccess: () => {
      toast.success("Status updated.");
      invalidateLists();
    },
    onError: (err) => handleError(err, "Failed to update status."),
  });

  const roleMutation = useMutation({
    mutationFn: (rolesList) => TenantUsersApi.updateRoles(user.id, rolesList),
    onSuccess: () => {
      toast.success("Roles updated.");
      setShowRoleModal(false);
      invalidateLists();
    },
    onError: (err) => handleError(err, "Failed to update roles."),
  });

  const deleteMutation = useMutation({
    mutationFn: () => TenantUsersApi.remove(user.id),
    onSuccess: () => {
      toast.success("User deleted.");
      queryClient.invalidateQueries({ queryKey: ["tenant-users"] });
      navigate("/home/team");
    },
    onError: (err) => handleError(err, "Failed to delete user."),
  });

  const handleToggleStatus = () => {
    if (!user) return;
    statusMutation.mutate(!user.active);
  };

  const handleDelete = () => {
    if (!user) return;
    showConfirmToast({
      message: `Delete ${user.firstName || user.email}?`,
      onConfirm: () => deleteMutation.mutate(),
      id: `delete-user-${user.id}`,
    });
  };

  if (detailQuery.isLoading && !user) {
    return (
      <div className="py-5">
        <Loader text="Loading user…" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <div className="alert alert-danger" role="alert">
        {detailQuery.error?.response?.data?.message || detailQuery.error?.message || "Failed to load user."}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="alert alert-warning" role="alert">
        User not found.
      </div>
    );
  }

  if (targetIsAgent && user.uuid) {
    return (
      <div className="py-5">
        <Loader text="Opening agent workspace…" />
      </div>
    );
  }

  const createdAt = user.createdAt ? new Date(user.createdAt).toLocaleString() : "—";
  const lastLogin = user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "Never";

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex align-items-center gap-2">
        <button className="btn btn-link px-0" type="button" onClick={() => navigate("/home/team")}>
          &larr; Back to team
        </button>
      </div>

      <UserProfileCard
        user={user}
        createdAt={createdAt}
        lastLogin={lastLogin}
        actions={
          <div className="d-flex flex-wrap align-items-center gap-2">
            {canEditProfile && (
              <button
                type="button"
                className="btn btn-primary rounded-pill"
                onClick={() => setShowEditModal(true)}
                disabled={updateMutation.isPending}
              >
                Edit profile
              </button>
            )}
            <MoreActionsDropdown
              label="More"
              items={(() => {
                const list = [];
                if (canManageRoles) {
                  list.push({
                    key: "roles",
                    label: "Change roles",
                    onClick: () => setShowRoleModal(true),
                    disabled: roleMutation.isPending,
                  });
                }
                if (list.length > 0 && (canToggleStatus || canDelete)) {
                  list.push({ type: "divider" });
                }
                if (canToggleStatus) {
                  list.push({
                    key: "status",
                    label: user.active ? "Deactivate" : "Activate",
                    onClick: handleToggleStatus,
                    disabled: statusMutation.isPending,
                  });
                }
                if (canDelete) {
                  list.push({
                    key: "delete",
                    label: "Delete",
                    onClick: handleDelete,
                    danger: true,
                    disabled: deleteMutation.isPending,
                  });
                }
                return list;
              })()}
            />
            {!canEditProfile && !canManageRoles && !canToggleStatus && !canDelete && (
              <div className="text-body-secondary small">
                You have view-only access to this user.
              </div>
            )}
          </div>
        }
      />

      {showEditModal && (
        <EditEntityModal
          title="Edit user"
          row={user}
          isBusy={updateMutation.isPending}
          onClose={() => setShowEditModal(false)}
          onSubmit={(dto) => updateMutation.mutate(dto)}
        />
      )}

      {showRoleModal && (
        <RoleManagerModal
          roles={user.roles}
          submitting={roleMutation.isPending}
          onClose={() => setShowRoleModal(false)}
          onSave={(updatedRoles) => roleMutation.mutate(updatedRoles)}
          disableOwnerRemoval={isSelf}
        />
      )}
    </div>
  );
}
